import type { Producto, Atributo } from '../../types/product.types';

/**
 * Filtra productos basado en un filtro rápido seleccionado
 * @param products - Array de productos a filtrar
 * @param quickFilterValue - Valor del filtro rápido seleccionado (formato: "label:value")
 * @returns Array de productos que coinciden con el filtro
 */
export function filterProductsByQuickFilter(
  products: Producto[],
  quickFilterValue: string | null
): Producto[] {
  if (!quickFilterValue || !products || products.length === 0) {
    return products || [];
  }

  // Parsear el valor del filtro (formato: "label:value")
  const [filterLabel, filterValue] = quickFilterValue.split(':');

  if (!filterLabel || !filterValue) {
    return products;
  }

  return products.filter((product) => {
    if (!product.atributos) {
      return false;
    }

    // Buscar si el producto tiene un atributo que coincida con el filtro
    return product.atributos.some(
      (atributo) =>
        atributo.quick_filter === true &&
        atributo.label === filterLabel &&
        atributo.value === filterValue
    );
  });
}

/**
 * Extrae todos los filtros rápidos disponibles de una lista de productos
 * @param products - Array de productos
 * @returns Array de filtros rápidos únicos con sus conteos
 */
export function extractQuickFilters(products: Producto[]): Array<{
  label: string;
  value: string;
  count: number;
  filterKey: string;
}> {
  if (!products || products.length === 0) {
    return [];
  }

  const quickFilterMap = new Map<
    string,
    {
      label: string;
      value: string;
      count: number;
      filterKey: string;
    }
  >();

  products.forEach((product) => {
    if (product.atributos) {
      product.atributos.forEach((atributo) => {
        if (atributo.quick_filter && atributo.value) {
          const filterKey = `${atributo.label}:${atributo.value}`;

          if (quickFilterMap.has(filterKey)) {
            // Incrementar el contador si ya existe
            const existingFilter = quickFilterMap.get(filterKey)!;
            existingFilter.count++;
          } else {
            // Crear nuevo filtro
            quickFilterMap.set(filterKey, {
              label: atributo.value,
              value: atributo.value,
              count: 1,
              filterKey: filterKey,
            });
          }
        }
      });
    }
  });

  // Convertir a array y ordenar por count descendente
  return Array.from(quickFilterMap.values()).sort((a, b) => b.count - a.count);
}

/**
 * Obtiene el conteo de productos para un filtro rápido específico
 * @param products - Array de productos
 * @param quickFilterValue - Valor del filtro rápido
 * @returns Número de productos que coinciden con el filtro
 */
export function getQuickFilterCount(
  products: Producto[],
  quickFilterValue: string
): number {
  return filterProductsByQuickFilter(products, quickFilterValue).length;
}
